import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { formatDate } from '../../lib/dateFormat';
import { api, communicationsApi, historyTemplatesApi, type RenderHistoryTemplateResponse } from '../../lib/api';
import { createOrder, getLatestOrderForTrial, sendOffer } from '../../lib/orders';
import { fetchTrial, type TrialLessonDTO } from '../../lib/trials';
import { erstesPaket, getEnrollmentForTrial, type EnrollmentDTO } from '../../lib/enrollments';
import { stundensatzAus } from '../../lib/honorar';
import { masterdataApi, type UnterrichtsformItem } from '../../lib/courses';
import { getRoom, type Room } from '../../lib/rooms';
import WizardShell from '../ui/wizard-shell';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  WizardCard,
  WizardSection,
  WizardKVGrid,
  WizardKVItem,
} from '../ui/wizard-layout';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { computePlannedPeriodRange } from '../../lib/plannedPeriod';
import { useAuth } from '../auth/use-auth';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  standortId?: number;
  onUpdated?: (trial: TrialLessonDTO) => void;
};

type StepState = 1 | 2 | 3;
type StepDelta = -1 | 1;

type OrderPreview = {
  id: number;
  status: string;
  plannedStartDate?: string | null;
  unterrichtsformId?: number | null;
};

type EnrollmentPreviewPackage = {
  id: string;
  name: string;
  units: number;
  special?: boolean;
  totalCentsImmediate?: number;
  totalCents?: number;
  currency?: string;
};

type EnrollmentPreviewPackagesResponse = {
  currency?: string;
  packages?: EnrollmentPreviewPackage[];
};

type TeacherPersonPreview = {
  salutation?: string | null;
  name?: string | null;
  address?: string | null;
  structuredAddress?: {
    street?: string | null;
    postalCode?: string | null;
    city?: string | null;
  } | null;
  teacher?: {
    artRates?: Array<{
      unterrichtsform_art_id?: number | string;
      art_id?: number | string;
      rate?: number | string;
    }>;
  } | null;
  artRates?: Array<{
    unterrichtsform_art_id?: number | string;
    art_id?: number | string;
    rate?: number | string;
  }>;
};

function toMoney(cents?: number | null, currency = 'EUR'): string {
  if (typeof cents !== 'number') return '—';
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

function formatDateWithWeekday(date?: string | null): string {
  const value = String(date || '').trim();
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

function durationMinutesFromTrial(trial: TrialLessonDTO): number | null {
  const start = String(trial.startTime || '').slice(0, 5);
  const end = String(trial.endTime || '').slice(0, 5);
  if (!start || !end || !start.includes(':') || !end.includes(':')) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  const diff = endMin - startMin;
  return diff > 0 ? diff : null;
}

function splitStudentName(name?: string | null): { first: string; last: string } {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

const WEEKDAY_TO_WOCHENTAGS: Record<string, string> = {
  Montag: 'montags',
  Dienstag: 'dienstags',
  Mittwoch: 'mittwochs',
  Donnerstag: 'donnerstags',
  Freitag: 'freitags',
  Samstag: 'samstags',
  Sonntag: 'sonntags',
};

function wochentagsFromDate(date?: string | null): string {
  const value = String(date || '').trim();
  if (!value) return '';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'long' }).format(parsed);
  return WEEKDAY_TO_WOCHENTAGS[weekday] ?? weekday.toLowerCase();
}

function channelPillClass(active: boolean): string {
  return cn(
    'inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-medium',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-muted bg-muted/70 text-muted-foreground',
  );
}

const HISTORY_TEMPLATE_KEY = 'trial.offer.sent.history';

export default function TrialOfferWizardModal({
  open,
  onOpenChange,
  trial,
  standortId,
  onUpdated,
}: Readonly<Props>) {
  const { user } = useAuth();
  const [step, setStep] = useState<StepState>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [orderPreview, setOrderPreview] = useState<OrderPreview | null>(null);
  const [enrollment, setEnrollment] = useState<EnrollmentDTO | null>(null);
  const [teacherPerson, setTeacherPerson] = useState<TeacherPersonPreview | null>(null);
  const [unterrichtsformName, setUnterrichtsformName] = useState<string>('');
  const [unterrichtsformArtId, setUnterrichtsformArtId] = useState<number | null>(null);
  const [previewSpecialPackage, setPreviewSpecialPackage] = useState<EnrollmentPreviewPackage | null>(null);
  const [previewPackages, setPreviewPackages] = useState<EnrollmentPreviewPackage[]>([]);
  const [offerAddress, setOfferAddress] = useState<string>('—');

  const [sendEmailTeacher, setSendEmailTeacher] = useState(true);
  const [sendSmsTeacher, setSendSmsTeacher] = useState(false);
  const [emailSubjectTeacher, setEmailSubjectTeacher] = useState('');
  const [emailHtmlTeacher, setEmailHtmlTeacher] = useState('');
  const [emailTextTeacher, setEmailTextTeacher] = useState('');
  const [smsTextTeacher, setSmsTextTeacher] = useState('');

  const [historyHtml, setHistoryHtml] = useState('');

  const moveStep = (delta: StepDelta) => {
    setStep((prev) => {
      const next = (prev + delta) as StepState;
      return Math.min(3, Math.max(1, next)) as StepState;
    });
  };

  const teacherWpUserId = useMemo(() => Number.parseInt(String(trial.teacherId || ''), 10) || 0, [trial.teacherId]);

  const standort = useMemo(() => {
    const fromTrial = Number((trial as any)?.standort_id ?? 0);
    if (fromTrial > 0) return fromTrial;
    return standortId && standortId > 0 ? standortId : 0;
  }, [standortId, trial]);

  const offerLessonCount = useMemo(() => {
    if (!enrollment) return null;
    // Was der Kunde gebucht hat, vor dem Schalter „Startpaket hinzufügen“ (T-0284).
    const gebucht = erstesPaket(enrollment, previewPackages);
    if (gebucht && typeof gebucht.units === 'number' && gebucht.units > 0) return gebucht.units;
    if (enrollment.startpaketSelected) {
      if (previewSpecialPackage && typeof previewSpecialPackage.units === 'number') {
        return previewSpecialPackage.units;
      }
      return typeof enrollment.startpaketUnits === 'number'
        ? enrollment.startpaketUnits
        : (typeof enrollment.packageUnits === 'number' ? enrollment.packageUnits : null);
    }
    return typeof enrollment.packageUnits === 'number' ? enrollment.packageUnits : null;
  }, [enrollment, previewSpecialPackage, previewPackages]);

  const offerDurationMin = useMemo(
    () => enrollment?.durationMin || durationMinutesFromTrial(trial) || null,
    [enrollment?.durationMin, trial]
  );

  // Der Auftrag beschreibt den Unterricht, nicht die Probestunde: Zeit aus der
  // Anmeldung, solange sie eine hat.
  const offerTimes = useMemo(() => {
    const enrollmentStart = String(enrollment?.startTime || '').slice(0, 5);
    if (enrollmentStart && offerDurationMin) {
      const [hh, mm] = enrollmentStart.split(':').map(Number);
      if (!Number.isNaN(hh) && !Number.isNaN(mm)) {
        const endMinutes = hh * 60 + mm + offerDurationMin;
        const end = `${String(Math.floor(endMinutes / 60) % 24).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
        return { start: enrollmentStart, end };
      }
    }
    return {
      start: trial.startTime?.slice(0, 5) || '',
      end: trial.endTime?.slice(0, 5) || '',
    };
  }, [enrollment?.startTime, offerDurationMin, trial.startTime, trial.endTime]);

  const teacherArtRatePerLessonCents = useMemo(() => {
    if (!teacherPerson || !unterrichtsformArtId || !offerDurationMin || offerDurationMin <= 0) return null;
    const candidates = Array.isArray(teacherPerson.teacher?.artRates)
      ? teacherPerson.teacher?.artRates || []
      : (Array.isArray(teacherPerson.artRates) ? teacherPerson.artRates : []);
    const matched = candidates.find((entry) => {
      const art = Number(entry?.unterrichtsform_art_id ?? entry?.art_id ?? 0);
      return art === unterrichtsformArtId;
    });
    if (!matched) return null;
    const ratePerHourEuro = Number(matched.rate ?? 0);
    if (!Number.isFinite(ratePerHourEuro) || ratePerHourEuro <= 0) return null;
    const ratePerHourCents = Math.round(ratePerHourEuro * 100);
    return Math.round(ratePerHourCents * (offerDurationMin / 60));
  }, [offerDurationMin, teacherPerson, unterrichtsformArtId]);

  const offerTotalValueCents = useMemo(() => {
    if (!enrollment) return null;
    if (typeof teacherArtRatePerLessonCents === 'number' && typeof offerLessonCount === 'number' && offerLessonCount > 0) {
      return teacherArtRatePerLessonCents * offerLessonCount;
    }
    return null;
  }, [enrollment, offerLessonCount, teacherArtRatePerLessonCents]);

  const offerHonorarPerLessonCents = useMemo(() => {
    if (typeof teacherArtRatePerLessonCents === 'number' && teacherArtRatePerLessonCents > 0) {
      return teacherArtRatePerLessonCents;
    }
    return null;
  }, [teacherArtRatePerLessonCents]);

  const honorarMissing = offerHonorarPerLessonCents === null || offerHonorarPerLessonCents <= 0;
  const honorarArtLabel = unterrichtsformName || 'Unterrichtsform-Art';
  const honorarMissingMessage = `Angebot kann nicht gesendet werden: Bei ${trial.teacherName || 'Lehrer'} ist kein Honorar für ${honorarArtLabel} hinterlegt. Bitte im Lehrerprofil unter Honorare pflegen.`;

  // Beginn des Unterrichts (Auftragsstart), nicht das Datum der Probestunde.
  const offerStartDate = orderPreview?.plannedStartDate || enrollment?.startDate || trial.date || '';

  const offerPlannedPeriod = useMemo(
    () => computePlannedPeriodRange({
      startDate: offerStartDate,
      lessonCount: offerLessonCount,
      intervalWeeks: enrollment?.intervalWeeks ?? 1,
    }),
    [offerStartDate, enrollment?.intervalWeeks, offerLessonCount],
  );

  const offerLessonCountLabel = typeof offerLessonCount === 'number' ? String(offerLessonCount) : '—';
  const offerHonorarLabel = toMoney(offerHonorarPerLessonCents, enrollment?.currency || 'EUR');
  const offerServiceLabel = enrollment?.unterrichtsformId
    ? `${unterrichtsformName || `Unterrichtsform #${enrollment.unterrichtsformId}`} · ${offerLessonCountLabel} Termine`
    : `${trial.fachName || trial.instrument || 'Unterricht'} · ${offerLessonCountLabel} Termine`;
  const teacherAddressLines = useMemo(() => {
    const lines: string[] = [];
    const street = String(teacherPerson?.structuredAddress?.street || '').trim();
    const zip = String(teacherPerson?.structuredAddress?.postalCode || '').trim();
    const city = String(teacherPerson?.structuredAddress?.city || '').trim();
    const zipCity = [zip, city].filter(Boolean).join(' ').trim();
    if (street) lines.push(street);
    if (zipCity) lines.push(zipCity);
    if (lines.length === 0) {
      const fallback = String(teacherPerson?.address || '').trim();
      if (fallback) {
        fallback
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .forEach((line) => lines.push(line));
      }
    }
    return lines;
  }, [teacherPerson]);
  const teacherSalutationLine = useMemo(() => {
    const salutation = String(teacherPerson?.salutation || '').trim();
    const teacherName = String(teacherPerson?.name || trial.teacherName || 'Lehrer').trim();
    if (salutation) return `${salutation} ${teacherName}`.trim();
    return `Herr/Frau ${teacherName}`.trim();
  }, [teacherPerson, trial.teacherName]);

  const templateVars = useMemo(
    () => {
      const studentParts = splitStudentName(trial.studentName);
      const startDateValue = offerStartDate;
      const startTimeValue = offerTimes.start;
      const endTimeValue = offerTimes.end;
      return {
        trialId: trial.id,
        orderId: orderPreview?.id ?? null,
        neue_auftragsnummer: orderPreview?.id ?? '',
        studentName: trial.studentName || '',
        schueler_vorname: studentParts.first,
        schueler_nachname: studentParts.last,
        teacherName: trial.teacherName || '',
        date: trial.date || '',
        startTime: startTimeValue,
        endTime: endTimeValue,
        start: startTimeValue,
        ende: endTimeValue,
        roomName: trial.roomName || '',
        roomClause: trial.roomName ? ` im Raum ${trial.roomName}` : '',
        raum: trial.roomName || '',
        raum_adresse: offerAddress,
        fachName: trial.fachName || trial.instrument || '',
        fach: trial.fachName || trial.instrument || '',
        unterrichtsform: unterrichtsformName || '',
        orderStatus: orderPreview?.status || '',
        plannedStartDate: orderPreview?.plannedStartDate || '',
        auftragsstart: startDateValue,
        neues_startdatum: startDateValue,
        wochentags: wochentagsFromDate(startDateValue || trial.date),
        honorar: offerHonorarLabel,
        neues_honorar: offerHonorarLabel,
        lessonCount: offerLessonCountLabel,
        locationAddress: offerAddress,
        alte_auftragsnummer: '',
        lehrer_absagen: '',
        aussetzen: '',
        auftragsende: '',
      };
    },
    [orderPreview, offerAddress, offerHonorarLabel, offerLessonCountLabel, enrollment?.startDate, trial, unterrichtsformName]
  );

  const composerGroups: RecipientGroup[] = useMemo(() => {
    if (!teacherWpUserId) return [];
    return [
      {
        key: 'teacher',
        label: 'Lehrer',
        recipients: [
          {
            id: `person:${teacherWpUserId}:`,
            type: 'person',
            wpUserId: teacherWpUserId,
            subIndex: null,
            name: trial.teacherName || `Lehrer #${teacherWpUserId}`,
          },
        ],
        templateKeys: { email: 'trial.offer.teacher', sms: 'trial.offer.teacher.sms' },
        vars: templateVars,
        defaultSelectedChannels: { email: true, sms: false },
      },
    ];
  }, [teacherWpUserId, trial.teacherName, templateVars]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSendEmailTeacher(true);
    setSendSmsTeacher(false);
    setEmailSubjectTeacher('');
    setEmailHtmlTeacher('');
    setEmailTextTeacher('');
    setSmsTextTeacher('');
    setHistoryHtml('');
    setOrderPreview(null);
    setEnrollment(null);
    setTeacherPerson(null);
    setUnterrichtsformName('');
    setUnterrichtsformArtId(null);
    setPreviewSpecialPackage(null);
    setPreviewPackages([]);
    setOfferAddress('—');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadPreviewData = async () => {
      setIsLoadingPreview(true);
      try {
        const [order, enrollmentData, forms] = await Promise.all([
          getLatestOrderForTrial(trial.id),
          getEnrollmentForTrial(trial.id).catch(() => null),
          masterdataApi.unterrichtsformen().catch(() => [] as UnterrichtsformItem[]),
        ]);
        if (cancelled) return;
        setEnrollment(enrollmentData);
        if (enrollmentData?.unterrichtsformId) {
          const form = (forms || []).find((entry) => entry.id === enrollmentData.unterrichtsformId);
          setUnterrichtsformName(form?.name || '');
          setUnterrichtsformArtId(form?.art_id ?? null);
        }

        const previewDuration = enrollmentData?.durationMin || durationMinutesFromTrial(trial);
        if (enrollmentData?.unterrichtsformId && previewDuration) {
          try {
            const pricing = await api.get<EnrollmentPreviewPackagesResponse>(
              `/api/enrollments/preview-packages?unterrichtsformId=${encodeURIComponent(String(enrollmentData.unterrichtsformId))}&duration=${encodeURIComponent(String(previewDuration))}`
            );
            if (!cancelled) {
              const allPackages = pricing?.packages || [];
              const selectedId = String(enrollmentData?.contingentPackageId || '').trim();
              const selected = selectedId !== '' ? allPackages.find((pkg) => String(pkg.id) === selectedId) : null;
              const special = allPackages.find((pkg) => pkg.special);
              setPreviewSpecialPackage(selected || special || allPackages[0] || null);
              setPreviewPackages(allPackages);
            }
          } catch {
            if (!cancelled) setPreviewSpecialPackage(null);
          }
        }

        const roomIdNum = Number.parseInt(String(trial.roomId || ''), 10);
        const standortIdNum = Number.parseInt(String((trial as any)?.standort_id || standortId || ''), 10);
        if (teacherWpUserId > 0) {
          try {
            const person = await api.get<TeacherPersonPreview>(`/api/personen/${teacherWpUserId}`);
            if (!cancelled) setTeacherPerson(person || null);
          } catch {
            if (!cancelled) setTeacherPerson(null);
          }
        }
        let room: Room | null = null;
        let standortNameText = '';
        try {
          if (!Number.isNaN(roomIdNum) && roomIdNum > 0) {
            room = await getRoom(roomIdNum);
          }
        } catch {
          room = null;
        }
        try {
          if (!Number.isNaN(standortIdNum) && standortIdNum > 0) {
            const standortData = await api.get<{ name?: string }>(`/api/standorte/${standortIdNum}`);
            standortNameText = String(standortData?.name || '').trim();
          }
        } catch {
          standortNameText = '';
        }
        if (!cancelled) {
          const roomAddress = room
            ? [
                room.address_street ? `${room.address_street}${room.address_house_number ? ` ${room.address_house_number}` : ''}` : '',
                [room.address_postal_code, room.address_city].filter(Boolean).join(' '),
              ].filter((line) => String(line || '').trim() !== '').join(', ')
            : '';
          const roomName = String(room?.name || trial.roomName || '').trim();
          const addressLines = [
            standortNameText ? `FIT IN music ${standortNameText}` : 'FIT IN music',
            roomAddress,
            roomName,
          ].filter((line) => line && line.trim() !== '');
          setOfferAddress(addressLines.length > 0 ? addressLines.join('\n') : '—');
        }
        if (order) {
          setOrderPreview({
            id: order.id,
            status: order.status,
            plannedStartDate: order.plannedStartDate || null,
            unterrichtsformId: order.unterrichtsformId || null,
          });
        }
      } catch {
        // Best-effort: Vorschau bleibt ohne Order-Details nutzbar.
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    };
    void loadPreviewData();
    return () => {
      cancelled = true;
    };
  }, [open, standortId, teacherWpUserId, trial]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadTemplatePreview = async () => {
      try {
        const rendered: RenderHistoryTemplateResponse = await historyTemplatesApi.render({
          template_key: HISTORY_TEMPLATE_KEY,
          standort_id: standort || undefined,
          vars: templateVars,
        });
        if (cancelled) return;
        setHistoryHtml(rendered.history || '');
      } catch {
        if (!cancelled) {
          const defaultText = `Angebot an Lehrer versendet: ${trial.teacherName || 'Lehrer'} für ${trial.studentName} am ${formatDate(trial.date)}.`;
          setHistoryHtml(defaultText);
        }
      }
    };
    void loadTemplatePreview();
    return () => {
      cancelled = true;
    };
  }, [open, templateVars, trial.date, trial.studentName, trial.teacherName, standort]);

  const canProceedStep2 = teacherWpUserId > 0 && (sendEmailTeacher || sendSmsTeacher);
  const canSubmit = sendEmailTeacher
    && emailSubjectTeacher.trim().length > 0
    && emailHtmlTeacher.trim().length > 0;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let order = await getLatestOrderForTrial(trial.id);
      if (!order) {
        order = await createOrder(trial.id, {
            plannedStartDate: enrollment?.startDate || trial.date,
            unterrichtsformId: enrollment?.unterrichtsformId || undefined,
          });
      }
      if (!order?.id) {
        throw new Error('Auftrag konnte nicht angelegt werden');
      }
      const wasAlreadySent = order.status === 'versendet';
      // Gab es noch keinen Auftrag, kannte die Vorschau keine Nummer: In der
      // Historie stand „Auftrag #.“ (T-0300). Jetzt steht sie fest.
      const nummer = String(order.id);
      await sendOffer(order.id, {
        email: {
          subjectTeacher: emailSubjectTeacher,
          htmlTeacher: emailHtmlTeacher,
          textTeacher: emailTextTeacher || undefined,
          // Die Platzhalter im komponierten Text füllt der Server — mit genau den
          // Werten, die hier in der Vorschau standen, und der jetzt bekannten Nummer.
          vars: { ...templateVars, orderId: order.id, neue_auftragsnummer: nummer },
        },
        history: {
          type: 'trial.offer.sent',
          description: historyHtml.replace(/Auftrag #(?!\d)/g, `Auftrag #${nummer}`),
        },
      });

      if (sendSmsTeacher && teacherWpUserId > 0 && smsTextTeacher.trim()) {
        await communicationsApi.send({
          type: 'sms',
          recipients: [{ wpUserId: teacherWpUserId }],
          content: { text: smsTextTeacher.trim() },
          meta: { source: 'trial-offer-wizard', trialId: trial.id },
        });
      }

      const refreshed = await fetchTrial(trial.id);
      if (refreshed) onUpdated?.(refreshed);
      toast.success(wasAlreadySent ? 'Angebot war bereits versendet' : 'Angebot wurde versendet');
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Angebot konnte nicht gesendet werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Angebot senden"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Angebotsvorschau' }, { label: 'Kommunikation' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? () => moveStep(-1) : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? () => moveStep(1) : undefined}
      canNext={step === 1 ? !honorarMissing : step === 2 ? canProceedStep2 : false}
      onPrimary={step === 3 ? handleSubmit : undefined}
      primaryLabel={isSubmitting ? 'Sende…' : 'Absenden'}
      canPrimary={step === 3 && canSubmit && !isSubmitting}
      isLoading={isSubmitting}
    >
      <div className="space-y-4 py-4 motion-safe:animate-fade-up">
        {step === 1 ? (
          <WizardCard>
            {honorarMissing ? (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Honorar fehlt</AlertTitle>
                <AlertDescription>{honorarMissingMessage}</AlertDescription>
              </Alert>
            ) : null}
            <WizardSection title="Angebotsvorschau">
              <div className="rounded-md border bg-white p-8 text-black shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-2xl font-bold leading-none">FIT IN music</div>
                    <div className="mt-1 text-base font-semibold">Deine Musikschule</div>
                    <div className="mt-5 text-[12px] leading-5">
                      FIT IN music GmbH &amp; Co. KG · Jasperallee 33 · 38102 Braunschweig
                    </div>
                  </div>
                  <div className="text-right text-[13px] leading-6">
                    <div>Braunschweig, den {trial.date ? formatDate(trial.date) : '—'}</div>
                  </div>
                </div>

                <div className="mt-5 text-[14px] leading-6">
                  <div>{teacherSalutationLine}</div>
                  {teacherAddressLines.length > 0 ? (
                    teacherAddressLines.map((line) => (
                      <div key={line}>{line}</div>
                    ))
                  ) : (
                    <div>Adressdaten aus Lehrerprofil</div>
                  )}
                </div>

                <div className="mt-7">
                  <div className="text-[37px] font-semibold leading-tight tracking-tight">Angebot</div>
                </div>

                <div className="mt-7 border-b border-black pb-2 text-[17px] leading-8">
                  <div>Sehr geehrte/r {trial.teacherName || 'Lehrer'},</div>
                  <div>hiermit unterbreiten wir Ihnen folgendes Angebot:</div>
                </div>

                <div className="mt-4 border border-black/70">
                  {[
                    ['Schüler/in', trial.studentName || '—'],
                    [getSubjectLabels().singular, trial.fachName || trial.instrument || '—'],
                    ['Leistung', offerServiceLabel],
                    ['Anzahl Termine', offerLessonCountLabel],
                    // Bezeichnungen seit T-0282: Stundensatz pro 60 Minuten, dazu je Termin.
                    ['Stundensatz', toMoney(stundensatzAus(offerHonorarPerLessonCents, offerDurationMin), enrollment?.currency || 'EUR')],
                    ['Honorar je Termin', offerHonorarLabel],
                    ['Gesamtbetrag', toMoney(offerTotalValueCents, enrollment?.currency || 'EUR')],
                    ['Eingeplanter Zeitraum', offerPlannedPeriod.label],
                    ['Termin', `${offerTimes.start || '--:--'} - ${offerTimes.end || '--:--'} Uhr`],
                    ['Adresse', offerAddress],
                  ].map(([label, value]) => (
                    <div key={label} className="grid grid-cols-[220px_1fr] border-b border-black/40 text-[14px] leading-6 last:border-b-0">
                      <div className="border-r border-black/40 px-3 py-1.5 font-semibold">{label}</div>
                      <div className="px-3 py-1.5 whitespace-pre-line">{value}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-7 border-t border-black pt-3">
                  <div className="text-[16px] font-semibold">Auftragsabschluss</div>
                  <div className="mt-2 grid grid-cols-2 gap-8 text-[14px] leading-6">
                    <div>
                      <div className="font-semibold">Auftraggeber</div>
                      {/* Wer den Auftrag erteilt, im Auftrag der Schule (T-0281). */}
                      <div>{user?.name ? `i. A. ${user.name}` : 'Office-Team'}</div>
                      <div>FIT IN music GmbH &amp; Co. KG</div>
                    </div>
                    <div>
                      <div className="font-semibold">Auftragnehmer</div>
                      <div>{trial.teacherName || 'Lehrer'}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 border-t border-black pt-4 text-[12px] leading-5">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="font-semibold">FIT IN music GmbH &amp; Co. KG</div>
                      <div>Jasperallee 33 · 38102 Braunschweig</div>
                      <div>Tel. +49 (0) 609 55 66-0</div>
                    </div>
                    <div className="text-right">
                      <div>Amtsgericht Braunschweig · HRA 201595</div>
                      <div>persönlich haftende Gesellschafterin:</div>
                      <div>FIT IN Verwaltung GmbH · HRB 205015</div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-xs text-muted-foreground">
                  Vorschau-Modus: Darstellung orientiert sich am späteren Auftrags-PDF.
                </div>
              </div>
            </WizardSection>
            {isLoadingPreview ? <div className="text-xs text-muted-foreground">Lade Vorschau-Daten…</div> : null}
          </WizardCard>
        ) : null}

        {step === 2 ? (
          <div className="space-y-3">
            {!teacherWpUserId ? (
              <p className="text-sm text-destructive">Kein Lehrer an der Probestunde hinterlegt. Angebot kann nicht versendet werden.</p>
            ) : null}
            {teacherWpUserId && standort > 0 ? (
              <CommunicationComposer
                active={open && step === 2}
                standortId={standort}
                groups={composerGroups}
                onDraftsChange={({ groups }) => {
                  const teacher = groups.teacher;
                  if (!teacher) return;
                  setSendEmailTeacher(!!teacher.selectedChannels.email);
                  setSendSmsTeacher(!!teacher.selectedChannels.sms);
                  setEmailSubjectTeacher(teacher.email.subjectTpl || '');
                  setEmailHtmlTeacher(teacher.email.htmlTpl || '');
                  setEmailTextTeacher(teacher.email.textTpl || '');
                  setSmsTextTeacher(teacher.sms.smsTpl || '');
                }}
              />
            ) : null}
            {teacherWpUserId && standort <= 0 ? <CommunicationStandortHint /> : null}
          </div>
        ) : null}

        {step === 3 ? (
          <WizardCard>
            <WizardSection title="Übersicht">
              <WizardKVGrid>
                <WizardKVItem label="Lehrer">{trial.teacherName || '—'}</WizardKVItem>
                <WizardKVItem label="Schüler">{trial.studentName || '—'}</WizardKVItem>
                <WizardKVItem label={getSubjectLabels().singular}>{trial.fachName || trial.instrument || '—'}</WizardKVItem>
                <WizardKVItem label="Termin">
                  {offerStartDate ? formatDate(offerStartDate) : '—'} · {offerTimes.start || '--:--'} - {offerTimes.end || '--:--'}
                </WizardKVItem>
                <WizardKVItem label="Order-ID">{orderPreview?.id || '—'}</WizardKVItem>
                <WizardKVItem label="Status nach Versand">versendet</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                {composerGroups.map((group) => (
                  <WizardKVItem key={group.key} label={group.label}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{group.recipients[0]?.name || group.label}</span>
                      <span className={channelPillClass(sendEmailTeacher)}>E-Mail</span>
                      <span className={channelPillClass(sendSmsTeacher)}>SMS</span>
                    </div>
                  </WizardKVItem>
                ))}
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Historie">
              <div className="space-y-2">
                <Label>Historieneintrag (Template)</Label>
                <Textarea
                  value={historyHtml}
                  onChange={(e) => setHistoryHtml(e.target.value)}
                  className="min-h-[110px]"
                />
              </div>
            </WizardSection>
          </WizardCard>
        ) : null}
      </div>
    </WizardShell>
  );
}
